import React, { FC, memo, CSSProperties, useMemo, useState, useEffect, useReducer } from "react";

import { PricingDisplay } from "@/components/library/PricingDisplay";
import LotCard from "@/components/ui/LotCard";
import QuickBidPopover from "@/components/ui/QuickBidPopover";
import { TooltipInfoContent } from "@/components/library/TooltipInfoContent";
import { AuctionTypeEnum } from "@/lib/utils/AuctionTypeEnum";
import { BiddingTable } from "../../containers/BiddingTable";
import { GeneralPopper } from "../../composite/popper/GeneralPopper";
import { MyActivityWishlist } from "../../containers/MyActivity";
import { useNavigator } from "@/lib/hooks/useNavigator";
import { getHighestBidColour, getHighestBidSuffix } from '@/lib/utils/General';
import { LotStatus } from "../../../lib/utils/LotStatusEnum";
import { useConfigureSoldBanner } from "@/lib/hooks/useConfigureSoldBanner";
import { getNewDateWithOffset, } from "prembid-shared-library-npm/helpers";
import { onReceiveMessage, clearOnReceiveMessage } from "prembid-shared-library-npm/signalr";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/Store";
import { MessageSubject } from "prembid-shared-library-npm/types";
import { useFinalizing, useConfigureCounter, useParticipateStatus } from "prembid-shared-library-npm/hooks";
import { PrembidLocalStorage } from '@/lib/utils/PrembidLocalStorage'

export interface CompactLotCardProps {
    containerStyle?: CSSProperties;
    lot: any;
    startDateTimeAt: any;
    hasExtensions: boolean;
    commission: number;
    lotFeatures?: any;
    auctionType: number;
    overrideGuidePriceRulesAndAlwaysHide: boolean;
    registrationStatus: any;
    overrideShowBidButtonForCompletedLots: boolean;
    auctionId: string;
    wishListStatus: boolean;
    //TODO: extend to be an enum once we know the card variants
    variant: any;
    settings: any;
    highestBid: number;
    highestBidProfileId: string | null;
    extendedBy: number;
}

const CompactLotCard: FC<CompactLotCardProps> = ({
    lot: {
        imageUrl,
        id: lotId,
        endDateTimeAt,
        stcStatus,
        name: header,
        showReserve,
        isActive,
        guidePrice,
        startingPrice,
        number: lotNumber,
    },
    startDateTimeAt,
    hasExtensions,
    commission,
    lotFeatures,
    auctionType,
    overrideGuidePriceRulesAndAlwaysHide,
    registrationStatus,
    overrideShowBidButtonForCompletedLots,
    auctionId,
    wishListStatus,
    settings,
    highestBid,
    highestBidProfileId,
    extendedBy,
    lot
}) => {

    const currentDateTimeOffset = useSelector((state: RootState) => state.settings.serverDateTimeDifference);
    const profileId = useSelector((state: RootState) => state.loginForm.changeProfileId);
    const { participateStatus, invalidateParticipateStatus, loading } = useParticipateStatus({
        profileId,
        auctionId
    });

    const { btnViewLotDetailsRoute } = useNavigator({
        lotId,
        auctionId,
    });

    const now = getNewDateWithOffset(currentDateTimeOffset);
    const start = useMemo(() => new Date(startDateTimeAt), [startDateTimeAt]);


    const [end, setEnd] = useState<Date>();

    const reserveMet = (reservePrice: number, highestBid: number) => {
        const tempReservePrice = reservePrice ? Number(reservePrice) : 0;
        return tempReservePrice > 0 ? Number(highestBid) >= tempReservePrice : true;
    };

    const [liveUpdates, setLiveUpdates] = useState({
        highestBid: highestBid,
        highestBidProfileId: lot?.winningProfileId,
        isExtended: hasExtensions,
        reserveMet: reserveMet(lot?.reservePrice, lot?.overrideWinningBidAmount ?? lot?.winningRegisteredProfileAmount),
        reservePrice: lot?.reservePrice,
        startingPrice,
        extendedBy: extendedBy,
    });

    useEffect(() => {
        setLiveUpdates((current) => ({
            ...current,
            highestBid: highestBid,
            highestBidProfileId: lot?.winningProfileId,
            reserveMet: reserveMet(lot?.reservePrice, lot?.overrideWinningBidAmount ?? lot?.winningRegisteredProfileAmount),
            isExtended: hasExtensions,
            reservePrice: lot?.reservePrice,
            startingPrice,
            extendedBy: extendedBy
        }));
    }, [hasExtensions, highestBid, lot?.winningProfileId, highestBidProfileId, lot?.overrideWinningBidAmount, lot?.reservePrice, lot?.winningRegisteredProfileAmount, startingPrice, extendedBy])

    useEffect(() => {
        if (!endDateTimeAt) return;
        const endTime = new Date(endDateTimeAt)
        if (liveUpdates?.extendedBy) endTime.setTime(endTime.getTime() + ((liveUpdates.extendedBy ?? extendedBy ?? 0) * 1000))
        setEnd(new Date(endTime));
    }, [endDateTimeAt, liveUpdates?.extendedBy]);

    const { countdownDate, countdownHeader, status, previousStatus } = useConfigureCounter({
        start,
        end,
    });

    const { soldBanner, handleShowSoldBanner } = useConfigureSoldBanner(
        lot,
        liveUpdates
    );

    useEffect(() => {
        handleShowSoldBanner(lot, liveUpdates, status);
    }, [handleShowSoldBanner, liveUpdates, lot, status]);

    useEffect(() => {
        const callbackId = onReceiveMessage((message: string) => {
            if (message) {
                var payload = JSON.parse(message);

                if (payload?.data) {
                    const messageSubject = payload.data['messageSubject'];
                    const messageAuctionId = payload.data['auctionId'];
                    const messageLotId = payload.data['lotId'];
                    const messageAmount = payload.data['amount'];
                    const messageProfileId = payload.data['profileId'];
                    const messageExtendedBy = payload.data['extendedBy'];

                    if (messageSubject === MessageSubject.UpdateRegisteredProfile && messageAuctionId === auctionId && messageProfileId === PrembidLocalStorage.currentProfile.toString()) {
                        invalidateParticipateStatus();
                    }

                    if (messageLotId === lotId) {
                        if (messageSubject === MessageSubject.BidUpdated) {
                            const winningRegisteredProfileAmount = payload.data['winningRegisteredProfileAmount'];
                            const winningProfileId = payload.data['winningProfileId'];

                            setLiveUpdates(current => ({
                                ...current,
                                highestBid: winningRegisteredProfileAmount,
                                highestBidProfileId: winningProfileId,
                                reserveMet: reserveMet(current?.reservePrice, winningRegisteredProfileAmount),
                            }));
                        }
                    }

                    if (messageAuctionId === auctionId && messageLotId === lotId) {
                        if (messageSubject === MessageSubject.CreateBid) {
                            setLiveUpdates(current => ({
                                ...current,
                                highestBid: messageAmount,
                                highestBidProfileId: messageProfileId,
                                reserveMet: reserveMet(current?.reservePrice, messageAmount),
                            }));

                            if (messageExtendedBy !== "" && messageExtendedBy !== undefined) {
                                setLiveUpdates(current => ({
                                    ...current,
                                    isExtended: true,
                                    extendedBy: Number(messageExtendedBy),
                                }));
                            }
                        }

                        if (messageSubject === MessageSubject.UpdatedStartPrice) {
                            setLiveUpdates((current) => ({
                                ...current,
                                startingPrice: payload.data['startingPrice'],
                            }));
                        }

                        if (messageSubject === MessageSubject.UpdatedReservePrice) {
                            setLiveUpdates((current) => ({
                                ...current,
                                reservePrice: Number(payload.data["reservePrice"]),
                                reserveMet: reserveMet(payload.data["reservePrice"], current?.highestBid),
                            }));
                        }

                        if (messageSubject === MessageSubject.ManuallyExtendedLot) {
                            setLiveUpdates((current) => ({
                                ...current,
                                isExtended: true,
                                extendedBy: Number(messageExtendedBy),
                            }));
                        }
                    }
                }
            }
        });
        return () => clearOnReceiveMessage(callbackId);
    }, [])

    const [quickBidAnchorEl, setQuickBidAnchorEl] = useState<any>(null);
    const [infoAnchorEl, setInfoAnchorEl] = useState<any>(null);

    const { finalizingText, setValidating, validating } = useFinalizing({
        auctionType,
        lotLocale: settings.LotName,
    });

    useEffect(() => {
        if (previousStatus !== undefined && status === LotStatus.COMPLETED && !lot?.sold) {
            setValidating(true);
            setTimeout(() => setValidating(false), 3000);
        }
    }, [status]);


    // Show the user's own accepted quick / custom bid on the card right away, as the bidding page does (and as on mobile);
    // the live update that follows confirms it, or replaces it if someone outbid them in the meantime.
    const handleBidPlaced = (amount: number) => {
        setLiveUpdates((current: any) =>
            amount > Number(current?.highestBid ?? 0)
                ? { ...current, highestBid: amount, highestBidProfileId: PrembidLocalStorage.currentProfile, reserveMet: reserveMet(current?.reservePrice, amount) }
                : current
        );
    };

    const isTender = Number(auctionType) === Number(AuctionTypeEnum.Tender.toString());
    const isStreamed = Number(auctionType) === Number(AuctionTypeEnum.Streamed.toString());

    return (
        <LotCard
            variant="compact"
            imgSrc={imageUrl}
            onOpen={btnViewLotDetailsRoute}
            lotNumber={lotNumber}
            title={header}
            isActive={isActive}
            validating={validating}
            finalizingText={finalizingText}
            soldBannerText={soldBanner?.showBanner === true ? soldBanner?.bannerText : undefined}
            now={now}
            start={start}
            end={end}
            isExtended={liveUpdates?.isExtended}
            stcStatus={stcStatus}
            commission={commission}
            showReserve={showReserve}
            reserveMet={liveUpdates?.reserveMet}
            lotFeatures={lotFeatures}
            onInfoClick={(e: any) => setInfoAnchorEl(e.currentTarget)}
            infoPopper={infoAnchorEl && (
                <GeneralPopper
                    popperStyle={{ zIndex: 11 }}
                    headerText={"Info"}
                    anchorEl={infoAnchorEl}
                    element={<TooltipInfoContent isActive={isActive} now={now} start={start} end={end as Date} hasExtensions={liveUpdates?.isExtended} auctionType={auctionType} settings={settings} validating={validating} />}
                    handleGeneralPopperClose={() => setInfoAnchorEl(null)}
                />
            )}
            countdownDate={countdownDate}
            countdownHeader={countdownHeader}
            getNow={() => getNewDateWithOffset(currentDateTimeOffset).getTime()}
            wishlist={<MyActivityWishlist lotId={lotId} isOnWishlist={wishListStatus} utilityClasses="pb-wish pb-wish--small" size={12} />}
            pricing={
                <PricingDisplay
                    variant="compact"
                    auctionType={auctionType}
                    guidePrice={guidePrice?.toString()}
                    overrideGuidePriceRulesAndAlwaysHide={overrideGuidePriceRulesAndAlwaysHide}
                    registrationStatus={participateStatus?.status ?? registrationStatus}
                    openingBid={liveUpdates?.startingPrice?.toString()}
                    highestBid={liveUpdates?.highestBid}
                    highestBidColour={getHighestBidColour(liveUpdates?.highestBidProfileId)}
                    highestBidSuffix={getHighestBidSuffix(liveUpdates?.highestBidProfileId)}
                />
            }
            actions={isActive && (
                <div className="pb-row">
                    <div>
                        <button type="button" className="pb-btn pb-btn-primary" onClick={btnViewLotDetailsRoute}>
                            {isStreamed ? "Info" : isTender ? "Tender/Info" : "Bid/Info"}
                        </button>
                    </div>
                    {!isStreamed && (
                        <div>
                            <button type="button" className="pb-btn pb-btn-ink" aria-haspopup="true" onClick={(e: any) => setQuickBidAnchorEl(quickBidAnchorEl ? null : e.currentTarget)}>
                                {isTender ? "Quick Tender" : "Quick Bid"}
                            </button>
                            <QuickBidPopover onBidPlaced={handleBidPlaced} anchorEl={quickBidAnchorEl} onClose={() => setQuickBidAnchorEl(null)} auctionId={auctionId} lotId={lotId} isTender={isTender} />
                        </div>
                    )}
                </div>
            )}
        />
    );
};

export default memo(CompactLotCard);
